import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Webhook, Copy, RefreshCw, Loader2, Check, X, FlaskConical, Save, ScrollText,
  Power, ListChecks, AlertTriangle, Plus, Trash2, Pencil, Users,
} from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { useAuth, can, isRestricted } from '@/hooks/useAuth';

interface WebhookFieldMap {
  awb?: string; statusCode?: string; statusText?: string; eventTime?: string; location?: string; ndrCode?: string;
  edd?: string; chargedWeight?: string; legType?: string;
}

const FIELD_DEFS: { key: keyof WebhookFieldMap; label: string; required?: boolean; hint: string }[] = [
  { key: 'awb', label: 'AWB / LR / Tracking No.', required: true, hint: 'The field that holds the shipment number we match on our order.' },
  { key: 'statusCode', label: 'Status Code', required: true, hint: 'The raw status code/text from the courier (mapped to your status below).' },
  { key: 'statusText', label: 'Status Description', hint: 'Optional human-readable remark saved on the tracking event.' },
  { key: 'eventTime', label: 'Event Time', hint: 'Optional scan timestamp. If empty we use the time we received it.' },
  { key: 'location', label: 'Location', hint: 'Optional scan location/city.' },
  { key: 'ndrCode', label: 'NDR / Reason Code', hint: 'Optional. On a failed-delivery status, opens an NDR if this code is mapped.' },
  { key: 'edd', label: 'EDD (Expected Delivery Date)', hint: 'Optional. Saved as the order’s expected delivery date.' },
  { key: 'chargedWeight', label: 'Charged Weight (kg)', hint: 'Optional. The courier’s billed weight — saved as the order’s captured weight.' },
  { key: 'legType', label: 'Leg Type (forward / return)', hint: 'Optional but important. The field that says which way the parcel is going — Delhivery sends UD/DL/RT/LT in Shipment.Status.StatusType. A value of RT/RTO/RETURN puts the shipment into RTO In Transit, because couriers reuse the same movement codes on the way home.' },
];

export function CourierWebhooksPage() {
  const user = useAuth((s) => s.user);
  const restricted = isRestricted(user);
  const canAdd = !restricted || can(user, 'Courier Webhook', 'All', 'add');
  const canEdit = !restricted || can(user, 'Courier Webhook', 'All', 'edit');

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Webhook size={22} /> Courier Webhook</h1>
          <p className="text-sm text-slate-500">Create one shared webhook URL per courier company + segment (e.g. all Delhivery B2C). The courier posts every update to it; we identify the shipment by AWB and update the right order automatically.</p>
        </div>
      </div>

      <GroupedWebhooksTab canAdd={canAdd} canEdit={canEdit} />
    </div>
  );
}

// ─────────────────────────────── Configure modal ───────────────────────────────

function CopyRow({ value }: { value: string }) {
  return (
    <div className="flex items-stretch gap-2">
      <code className="flex-1 px-3 py-2 bg-slate-900 text-slate-100 rounded-lg text-xs font-mono break-all">{value}</code>
      <button onClick={() => { navigator.clipboard.writeText(value); toast.success('Copied'); }}
        className="px-3 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-1">
        <Copy size={14} /> Copy
      </button>
    </div>
  );
}

// Flatten a JSON object into dot-paths → sample values (arrays use index 0), for the mapping helper.
function flattenPaths(obj: any, prefix = '', out: { path: string; sample: string }[] = [], depth = 0): { path: string; sample: string }[] {
  if (depth > 6 || obj == null) return out;
  if (Array.isArray(obj)) {
    if (obj.length) flattenPaths(obj[0], `${prefix}.0`, out, depth + 1);
    return out;
  }
  if (typeof obj === 'object') {
    for (const k of Object.keys(obj)) flattenPaths(obj[k], prefix ? `${prefix}.${k}` : k, out, depth + 1);
    return out;
  }
  out.push({ path: prefix, sample: String(obj).slice(0, 40) });
  return out;
}

function SamplePreview({ obj, path }: { obj: any, path: string }) {
  const v = path.split('.').reduce((a: any, s: string) => (a == null ? undefined : a[s]), obj);
  if (v == null || typeof v === 'object') return null;
  return <span className="ml-1 font-normal text-emerald-600">→ {String(v).slice(0, 30)}</span>;
}

function TestLine({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500 w-40">{label}</span>
      <span className={value ? 'text-slate-800 font-mono' : 'text-rose-500'}>{value || '— nothing —'}</span>
    </div>
  );
}

// ═══════════════════════════════ GROUPED WEBHOOKS ═══════════════════════════════
// One shared URL for several couriers of the same company + segment (e.g. all Delhivery B2C).
// On ingest the backend finds the order by AWB and routes to that order's courier.

interface GroupMember { id: number; name: string; code: string; orderType: 'B2C' | 'B2B'; }
interface GroupRow {
  id: number; name: string; orderType: 'B2C' | 'B2B'; courierIds: number[]; members: GroupMember[];
  webhookUrl: string; fieldMap: WebhookFieldMap | null; hasMapping: boolean; isActive: boolean; lastEventAt: string | null;
}

function GroupedWebhooksTab({ canAdd, canEdit }: { canAdd: boolean; canEdit: boolean }) {
  const qc = useQueryClient();
  const [formFor, setFormFor] = useState<GroupRow | 'new' | null>(null);
  const [configFor, setConfigFor] = useState<GroupRow | null>(null);
  const [logsFor, setLogsFor] = useState<GroupRow | null>(null);

  const { data: groups, isLoading } = useQuery({
    queryKey: ['courier-webhook-groups'],
    queryFn: async () => (await api.get<GroupRow[]>('/courier-webhook-groups')).data,
  });

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/courier-webhook-groups/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['courier-webhook-groups'] }); toast.success('Group deleted'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Delete failed'),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500 max-w-3xl">
          Create one webhook for a courier company + segment, pick the couriers it covers, and share the single URL with them.
          The courier sends every update to it; we identify the shipment by AWB and update the right order automatically.
        </p>
        {canAdd && (
          <button onClick={() => setFormFor('new')}
            className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 flex items-center gap-1.5 shrink-0">
            <Plus size={15} /> New group
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Group</th>
                <th className="text-left px-4 py-3">Segment</th>
                <th className="text-left px-4 py-3">Couriers</th>
                <th className="text-left px-4 py-3">Webhook</th>
                <th className="text-left px-4 py-3">Mapping</th>
                <th className="text-left px-4 py-3">Last Update</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={7} className="text-center py-10 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>)}
              {!isLoading && (groups ?? []).length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400">No webhook groups yet. Click <b>New group</b> to create one.</td></tr>
              )}
              {(groups ?? []).map((g) => (
                <tr key={g.id} className="border-t border-slate-100 hover:bg-slate-50/60 align-top">
                  <td className="px-4 py-3 font-medium text-slate-900">{g.name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${g.orderType === 'B2C' ? 'bg-sky-50 text-sky-700' : 'bg-violet-50 text-violet-700'}`}>{g.orderType}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {g.members.map((m) => (
                        <span key={m.id} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{m.name}</span>
                      ))}
                      {g.members.length === 0 && <span className="text-xs text-slate-400">none</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${g.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      <Power size={11} /> {g.isActive ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {g.hasMapping
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><Check size={13} /> Mapped</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-amber-600"><AlertTriangle size={13} /> Not mapped</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{g.lastEventAt ? new Date(g.lastEventAt).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setConfigFor(g)}
                        className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs flex items-center gap-1 hover:bg-slate-50">
                        <ListChecks size={13} /> Configure
                      </button>
                      {canEdit && (
                        <button onClick={() => setFormFor(g)}
                          className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs flex items-center gap-1 hover:bg-slate-50">
                          <Pencil size={13} /> Edit
                        </button>
                      )}
                      <button onClick={() => setLogsFor(g)}
                        className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs flex items-center gap-1 hover:bg-slate-50">
                        <ScrollText size={13} /> Logs
                      </button>
                      {canEdit && (
                        <button onClick={() => { if (confirm(`Delete group "${g.name}"? The shared URL will stop working.`)) del.mutate(g.id); }}
                          className="px-2 py-1.5 border border-rose-200 text-rose-600 rounded-lg text-xs hover:bg-rose-50">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {formFor && <GroupFormModal group={formFor === 'new' ? null : formFor} onClose={() => setFormFor(null)} />}
      {configFor && <GroupConfigModal group={configFor} canEdit={canEdit} onClose={() => setConfigFor(null)} />}
      {logsFor && <GroupLogsModal group={logsFor} onClose={() => setLogsFor(null)} />}
    </div>
  );
}

interface AvailCourier { id: number; name: string; code: string; orderType: 'B2C' | 'B2B'; isActive: boolean; inGroup: { id: number; name: string } | null; }

function GroupFormModal({ group, onClose }: { group: GroupRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const editing = !!group;
  const [name, setName] = useState(group?.name ?? '');
  const [orderType, setOrderType] = useState<'B2C' | 'B2B'>(group?.orderType ?? 'B2C');
  const [selected, setSelected] = useState<Set<number>>(new Set(group?.courierIds ?? []));
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const { data: couriers, isLoading } = useQuery({
    queryKey: ['wh-group-available', orderType],
    queryFn: async () => (await api.get<AvailCourier[]>('/courier-webhook-groups/available-couriers', { params: { orderType } })).data,
  });

  const toggle = (id: number) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), orderType, courierIds: Array.from(selected) };
      if (editing) return (await api.patch(`/courier-webhook-groups/${group!.id}`, { name: body.name, courierIds: body.courierIds })).data;
      return (await api.post('/courier-webhook-groups', body)).data;
    },
    onSuccess: (d: any) => {
      qc.invalidateQueries({ queryKey: ['courier-webhook-groups'] });
      if (editing) { toast.success('Group updated'); onClose(); }
      else { toast.success('Group created'); setCreatedUrl(d?.webhookUrl ?? null); }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  if (createdUrl) {
    return (
      <Modal isOpen onClose={onClose} title="Group created" subtitle="Share this single URL with the courier team." maxWidth="2xl">
        <div className="space-y-3">
          <label className="block text-xs font-medium text-slate-600">Webhook URL (HTTP POST, JSON body)</label>
          <CopyRow value={createdUrl} />
          <p className="text-xs text-slate-500">Next: open <b>Configure</b> on the group to map the payload fields (AWB + status code) and add the status codes.</p>
          <div className="flex justify-end"><button onClick={onClose} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">Done</button></div>
        </div>
      </Modal>
    );
  }

  const canSave = name.trim().length > 0 && selected.size > 0 && !save.isPending;
  return (
    <Modal isOpen onClose={onClose} title={editing ? `Edit group — ${group!.name}` : 'New webhook group'}
      subtitle="Pick the couriers this shared webhook covers (same company + segment)." maxWidth="2xl">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Group name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Delhivery B2C" className="input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Segment</label>
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-sm">
              {(['B2C', 'B2B'] as const).map((t) => (
                <button key={t} disabled={editing} onClick={() => { setOrderType(t); setSelected(new Set()); }}
                  className={`px-4 py-1.5 ${orderType === t ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'} ${t === 'B2B' ? 'border-l border-slate-200' : ''} disabled:opacity-60`}>{t}</button>
              ))}
            </div>
            {editing && <p className="text-[11px] text-slate-400 mt-1">Segment can't change after creation.</p>}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1"><Users size={13} /> Couriers in this group ({selected.size} selected)</label>
          <div className="border border-slate-200 rounded-lg max-h-64 overflow-auto divide-y divide-slate-100">
            {isLoading && <div className="py-8 text-center text-slate-400"><Loader2 size={16} className="mx-auto animate-spin" /></div>}
            {(couriers ?? []).map((c) => {
              const otherGroup = c.inGroup && (!editing || c.inGroup.id !== group!.id);
              return (
                <label key={c.id} className={`flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer ${otherGroup ? 'opacity-60' : ''}`}>
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="rounded" />
                  <span className="font-medium text-slate-800">{c.name}</span>
                  <span className="text-xs text-slate-400 font-mono">{c.code}</span>
                  {!c.isActive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">inactive</span>}
                  {otherGroup && <span className="ml-auto text-[11px] text-amber-600">already in "{c.inGroup!.name}"</span>}
                </label>
              );
            })}
            {!isLoading && (couriers ?? []).length === 0 && <div className="py-8 text-center text-slate-400 text-sm">No {orderType} couriers.</div>}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">A courier can also belong to another group; the last webhook that matches an AWB wins. Keep companies separate to avoid confusion.</p>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!canSave}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90 disabled:opacity-50">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editing ? 'Save changes' : 'Create group'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GroupConfigModal({ group, canEdit, onClose }: { group: GroupRow; canEdit: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'mapping' | 'status'>('mapping');
  const [fieldMap, setFieldMap] = useState<WebhookFieldMap>(group.fieldMap ?? {});
  const [sampleText, setSampleText] = useState('');
  const [isActive, setIsActive] = useState(group.isActive);
  const [testResult, setTestResult] = useState<any>(null);

  const parsedSample = useMemo(() => {
    if (!sampleText.trim()) return null;
    try { return JSON.parse(sampleText); } catch { return undefined; }
  }, [sampleText]);
  const paths = useMemo(() => (parsedSample ? flattenPaths(parsedSample).slice(0, 120) : []), [parsedSample]);

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/courier-webhook-groups/${group.id}`, {
      fieldMap, isActive, ...(parsedSample ? { samplePayload: parsedSample } : {}),
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['courier-webhook-groups'] }); toast.success('Saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });
  const regen = useMutation({
    mutationFn: async () => (await api.post(`/courier-webhook-groups/${group.id}/regenerate`, {})).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['courier-webhook-groups'] }); toast.success('New URL generated — update it with the courier'); onClose(); },
    onError: () => toast.error('Failed to regenerate'),
  });
  const test = useMutation({
    mutationFn: async () => {
      if (parsedSample === undefined) throw new Error('Sample payload is not valid JSON');
      return (await api.post(`/courier-webhook-groups/${group.id}/test`, { payload: parsedSample ?? {}, fieldMap })).data;
    },
    onSuccess: (d) => setTestResult(d),
    onError: (e: any) => toast.error(e?.message ?? 'Test failed'),
  });

  return (
    <Modal isOpen onClose={onClose} title={`Configure — ${group.name} (${group.orderType})`}
      subtitle="Share the URL with the courier, then map their payload. Status codes apply to every courier in the group." maxWidth="4xl">
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Shared webhook URL (give this to the courier — HTTP POST, JSON body)</label>
          <CopyRow value={group.webhookUrl} />
          <div className="flex items-center gap-3 mt-2">
            {canEdit && (
              <button onClick={() => { if (confirm('Regenerate the URL? The old one stops working immediately.')) regen.mutate(); }} disabled={regen.isPending}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                <RefreshCw size={12} className={regen.isPending ? 'animate-spin' : ''} /> Regenerate URL
              </button>
            )}
            <span className="text-xs text-slate-400">Covers: {group.members.map((m) => m.name).join(', ') || 'none'}</span>
            <label className="text-xs text-slate-600 flex items-center gap-1.5 ml-auto">
              <input type="checkbox" checked={isActive} disabled={!canEdit} onChange={(e) => setIsActive(e.target.checked)} className="rounded" />
              Active (receive updates)
            </label>
          </div>
        </div>

        <div className="border-b border-slate-200 flex gap-1">
          {([['mapping', 'Field Mapping'], ['status', 'Status Mapping']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{label}</button>
          ))}
        </div>

        {tab === 'mapping' && (
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Paste a sample payload the courier sends (JSON)</label>
              <textarea value={sampleText} onChange={(e) => { setSampleText(e.target.value); setTestResult(null); }}
                placeholder='{ "waybill": "123456", "Status": { "Code": "DLVD" } }'
                className="w-full h-40 px-3 py-2 border border-slate-200 rounded-lg font-mono text-xs" />
              {sampleText.trim() && parsedSample === undefined && (<p className="text-danger text-xs mt-1">Not valid JSON.</p>)}
              {paths.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-1">Detected fields (click to copy the path):</p>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-auto">
                    {paths.map((p) => (
                      <button key={p.path} title={`sample: ${p.sample}`}
                        onClick={() => { navigator.clipboard.writeText(p.path); toast.success(`Copied "${p.path}"`); }}
                        className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 font-mono">{p.path}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2.5">
              {FIELD_DEFS.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-slate-600">
                    {f.label} {f.required && <span className="text-danger">*</span>}
                    {parsedSample && fieldMap[f.key] && (<SamplePreview obj={parsedSample} path={fieldMap[f.key]!} />)}
                  </label>
                  <input list={paths.length ? 'wh-group-paths' : undefined}
                    value={fieldMap[f.key] ?? ''} disabled={!canEdit}
                    onChange={(e) => { setFieldMap((m) => ({ ...m, [f.key]: e.target.value })); setTestResult(null); }}
                    placeholder="e.g. Status.Code" className="input font-mono text-xs" />
                  <p className="text-[11px] text-slate-400">{f.hint}</p>
                </div>
              ))}
              {paths.length > 0 && (<datalist id="wh-group-paths">{paths.map((p) => <option key={p.path} value={p.path} />)}</datalist>)}
            </div>
          </div>
        )}

        {tab === 'status' && <GroupStatusMappingEditor groupId={group.id} canEdit={canEdit} />}

        {tab === 'mapping' && (
          <>
            <div className="flex items-center gap-2 border-t border-slate-100 pt-3">
              <button onClick={() => test.mutate()} disabled={test.isPending || !sampleText.trim()}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
                {test.isPending ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />} Test mapping
              </button>
              <div className="flex-1" />
              {canEdit && (
                <button onClick={() => save.mutate()} disabled={save.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90 disabled:opacity-60">
                  {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save mapping
                </button>
              )}
            </div>

            {testResult && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1.5">
                <div className="font-medium text-slate-700">Test result</div>
                <TestLine label="AWB / LR extracted" value={testResult.extracted?.awb} />
                <TestLine label="Raw status" value={testResult.extracted?.rawStatus} />
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-40">Order match</span>
                  {testResult.orderFound
                    ? <span className="text-emerald-700 font-medium">{testResult.order?.orderNo} (current #{testResult.order?.currentStatusId})</span>
                    : <span className="text-rose-600">No order found</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-40">Routed to courier</span>
                  {testResult.memberMatch
                    ? <span className="text-emerald-700 font-medium">{testResult.resolvedCourier?.name}</span>
                    : <span className="text-rose-600">AWB's courier is not in this group</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-40">Maps to our status</span>
                  {testResult.statusMapped
                    ? <span className="text-emerald-700 font-medium">{testResult.resolvedStatusLabel} (#{testResult.resolvedStatusId})</span>
                    : <span className="text-amber-600">Not mapped — add it under Status Mapping</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-40">Would update?</span>
                  {testResult.wouldUpdate ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check size={14} /> Yes</span>
                    : <span className="inline-flex items-center gap-1 text-rose-600"><X size={14} /> No</span>}
                </div>
                {(testResult.warnings ?? []).length > 0 && (
                  <ul className="list-disc pl-5 text-amber-600 text-xs pt-1">
                    {testResult.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function GroupStatusMappingEditor({ groupId, canEdit }: { groupId: number; canEdit: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['wh-group-status-map', groupId],
    queryFn: async () => (await api.get<{ statuses: { id: number; label: string }[]; mappings: { orderStatusId: number; codes: string[] }[]; memberCount: number }>(`/courier-webhook-groups/${groupId}/status-mappings`)).data,
  });
  const [draft, setDraft] = useState<Record<number, string>>({});

  const rowsToShow = useMemo(() => {
    if (!data) return [];
    const byId = new Map(data.mappings.map((m) => [m.orderStatusId, m.codes.join(', ')]));
    return data.statuses.map((s) => ({ ...s, codes: draft[s.id] ?? byId.get(s.id) ?? '' }));
  }, [data, draft]);

  const save = useMutation({
    mutationFn: async () => {
      const rows = rowsToShow
        .map((r) => ({ id: r.id, codes: r.codes.split(',').map((c) => c.trim()).filter(Boolean) }))
        .filter((r) => r.codes.length);
      return (await api.post(`/courier-webhook-groups/${groupId}/status-mappings`, { rows })).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wh-group-status-map', groupId] }); toast.success('Status mapping saved to all couriers in the group'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  if (isLoading) return <div className="py-8 text-center text-slate-400"><Loader2 size={18} className="mx-auto animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Enter the courier's raw status code(s) for each of your statuses, comma-separated. Saved to <b>all {data?.memberCount ?? 0} couriers</b> in this group at once.</p>
      <div className="max-h-80 overflow-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
            <tr><th className="text-left px-3 py-2 w-64">Your Status</th><th className="text-left px-3 py-2">Courier raw codes (comma-separated)</th></tr>
          </thead>
          <tbody>
            {rowsToShow.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-1.5 text-slate-700">{r.label} <span className="text-slate-300">#{r.id}</span></td>
                <td className="px-3 py-1.5">
                  <input value={r.codes} disabled={!canEdit}
                    onChange={(e) => setDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                    placeholder="e.g. DLVD, DEL, 401" className="input font-mono text-xs" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="flex justify-end">
          <button onClick={() => save.mutate()} disabled={save.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90 disabled:opacity-60">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save status mapping
          </button>
        </div>
      )}
    </div>
  );
}

function GroupLogsModal({ group, onClose }: { group: GroupRow; onClose: () => void }) {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['courier-webhook-group-logs', group.id],
    queryFn: async () => (await api.get<any[]>(`/courier-webhook-groups/${group.id}/logs`)).data,
  });
  const [open, setOpen] = useState<string | null>(null);
  const memberName = (cid: number | null) => group.members.find((m) => m.id === cid)?.name;
  const badge = (s: string) => ({
    processed: 'bg-emerald-50 text-emerald-700', received: 'bg-sky-50 text-sky-700',
    ignored: 'bg-amber-50 text-amber-700', failed: 'bg-rose-50 text-rose-700',
  } as Record<string, string>)[s] ?? 'bg-slate-100 text-slate-600';

  return (
    <Modal isOpen onClose={onClose} title={`Webhook Logs — ${group.name}`} subtitle="Most recent payloads received on the shared URL." maxWidth="4xl">
      <div className="flex justify-end mb-2">
        <button onClick={() => refetch()} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
          <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      {isLoading ? (
        <div className="py-10 text-center text-slate-400"><Loader2 size={18} className="mx-auto animate-spin" /></div>
      ) : (data ?? []).length === 0 ? (
        <div className="py-10 text-center text-slate-400 text-sm">No webhooks received yet.</div>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-auto">
          {(data ?? []).map((l) => (
            <div key={l.id} className="border border-slate-200 rounded-lg">
              <button onClick={() => setOpen(open === l.id ? null : l.id)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge(l.status)}`}>{l.status}</span>
                {l.courierId && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{memberName(l.courierId) ?? `#${l.courierId}`}</span>}
                <span className="text-xs text-slate-600 flex-1 truncate">{l.note ?? '—'}</span>
                <span className="text-xs text-slate-400">{new Date(l.createdAt).toLocaleString()}</span>
              </button>
              {open === l.id && (
                <pre className="px-3 py-2 bg-slate-900 text-slate-100 text-[11px] overflow-auto max-h-64 rounded-b-lg">{JSON.stringify(l.payload, null, 2)}</pre>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────── Status mapping (reuses backend courier-mappings) ─────────

// ─────────────────────────────── Logs modal ───────────────────────────────

